package com.milta.feature.admin.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.admin.domain.usecase.GetAdminPromptsUseCase
import com.milta.feature.admin.domain.usecase.ResetPromptUseCase
import com.milta.feature.admin.domain.usecase.UpdatePromptUseCase
import kotlinx.coroutines.launch

/**
 * ViewModel for the Admin "Prompts" screen (2026-10-03) — web-only. Each prompt's locked
 * [com.milta.feature.admin.domain.entity.AdminPrompt.contractFooter] is display-only here (no
 * intent ever changes it); only [AdminPromptsState.drafts] (the editable body) round-trips to the
 * backend, matching the backend split (PromptTemplate only ever stores the body, never the footer).
 */
class AdminPromptsViewModel(
    private val getAdminPrompts: GetAdminPromptsUseCase,
    private val updatePrompt: UpdatePromptUseCase,
    private val resetPrompt: ResetPromptUseCase,
) : MviViewModel<AdminPromptsState, AdminPromptsIntent>(AdminPromptsState()) {

    override fun onIntent(intent: AdminPromptsIntent) {
        when (intent) {
            AdminPromptsIntent.Load -> load()
            is AdminPromptsIntent.SelectTab -> setState { copy(selectedKey = intent.key) }
            is AdminPromptsIntent.UpdateDraft -> setState {
                copy(drafts = drafts + (intent.key to intent.text))
            }
            is AdminPromptsIntent.TogglePreview -> setState {
                val keys = previewKeys.toMutableSet()
                if (!keys.add(intent.key)) keys.remove(intent.key)
                copy(previewKeys = keys)
            }
            is AdminPromptsIntent.Save -> save(intent.key)
            is AdminPromptsIntent.Reset -> reset(intent.key)
        }
    }

    private fun load() {
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            getAdminPrompts().fold(
                onSuccess = { prompts ->
                    setState {
                        copy(
                            isLoading = false,
                            prompts = prompts,
                            drafts = prompts.associate { it.key to it.body },
                            selectedKey = selectedKey ?: prompts.firstOrNull()?.key,
                        )
                    }
                },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLoading = false, error = errorCode) }
                },
            )
        }
    }

    private fun save(key: String) {
        val draft = currentState.drafts[key] ?: return
        scope.launch {
            setState { copy(savingKeys = savingKeys + key, actionErrors = actionErrors - key) }
            updatePrompt(key, draft).fold(
                onSuccess = { updated ->
                    setState {
                        copy(
                            savingKeys = savingKeys - key,
                            prompts = prompts.map { if (it.key == key) updated else it },
                            drafts = drafts + (key to updated.body),
                        )
                    }
                },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(savingKeys = savingKeys - key, actionErrors = actionErrors + (key to errorCode)) }
                },
            )
        }
    }

    private fun reset(key: String) {
        scope.launch {
            setState { copy(savingKeys = savingKeys + key, actionErrors = actionErrors - key) }
            resetPrompt(key).fold(
                onSuccess = { updated ->
                    setState {
                        copy(
                            savingKeys = savingKeys - key,
                            prompts = prompts.map { if (it.key == key) updated else it },
                            drafts = drafts + (key to updated.body),
                        )
                    }
                },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(savingKeys = savingKeys - key, actionErrors = actionErrors + (key to errorCode)) }
                },
            )
        }
    }
}
